import { Scene } from "phaser";
import { mobileAndTabletCheck } from "../../helpers/gameobject-to-object-point.ts";

export class Preloader extends Scene {
  constructor() {
    super("Preloader");
  }

  init() {
    const w = window.innerWidth;
    const h = window.innerHeight;

    // Near-black backdrop
    this.add.graphics().fillStyle(0x05070a, 1).fillRect(0, 0, w, h);

    // RO car poster (loaded in Boot), cover-scaled and dimmed
    const car = this.add.image(w / 2, h / 2, "ro_car");
    car.setScale(Math.max(w / car.width, h / car.height));
    car.setAlpha(0.5);
    this.add.graphics().fillStyle(0x05070a, 0.5).fillRect(0, 0, w, h);

    // Cyan progress bar, anchored left so it grows rightward
    const barW = Math.min(468, w * 0.6);
    const barX = w / 2 - barW / 2;
    const barY = h * 0.82;
    this.add.rectangle(w / 2, barY, barW, 16).setStrokeStyle(1, 0x1fa8e0);
    const bar = this.add
      .rectangle(barX + 2, barY, 4, 12, 0x1fa8e0)
      .setOrigin(0, 0.5);

    //  Use the 'progress' event emitted by the LoaderPlugin to update the loading bar
    this.load.on("progress", (progress: number) => {
      bar.width = 4 + (barW - 8) * progress;
    });
  }

  preload() {
    //  Load the assets for the game - Replace with your own assets
    this.load.setPath("assets");
    this.load.font("ShallWe", "fonts/war-games.otf", "opentype");

    // Removed loading of old logo as we're now using text-based logo
    this.load.image("star", "star.png");

    // npc images
    this.load.image(`0a`, `sprites/npcs/0a.png`);
    this.load.atlas(
      `a-npc-0a`,
      `spritesheets/npcs/0A_NPC.png`,
      "spritesheets/npcs/npc_atlas.json",
    );
    //
    this.load.image(`samosa`, `sprites/npcs/samosa.png`);
    this.load.atlas(
      `a-npc-samosa`,
      `spritesheets/npcs/Samosa_NPC.png`,
      "spritesheets/npcs/npc_atlas.json",
    );
    //
    this.load.image(`cmd`, `sprites/npcs/cmd.png`);
    this.load.atlas(
      `a-npc-cmd`,
      `spritesheets/npcs/CMD_NPC.png`,
      "spritesheets/npcs/npc_atlas.json",
    );
    // generic
    for (let i = 1; i < 10; i++) {
      for (let dir of ["C", "E", "N", "S", "W"]) {
        this.load.image(
          `npc-${dir}-${i}`,
          `sprites/npcs/npc-${dir}-${i}.png`,
        );
        this.load.atlas(
          `a-npc-${dir}-${i}`,
          `spritesheets/npcs/NPC_${dir}${String(i).padStart(2, "0")}.png`,
          "spritesheets/npcs/npc_atlas.json",
        );
      }
    }

    // player image
    for (let i = 1; i <= 30; i++) {
      this.load.image(
        `king-${i}`,
        `sprites/Player_${String(i).padStart(2, "0")}.png`,
      );
      this.load.atlas(
        `a-player-${i}`,
        `spritesheets/PC_${String(i).padStart(2, "0")}.png`,
        "spritesheets/PC_30_atlas.json",
      );
    }

    // tilesets
    this.load.image("tilesNpc", "tilesets/NPC_All.png");
    this.load.image("tilesNpc32", "tilesets/32NPC_tileset.png");
    this.load.image("tilesTerrain", "tilesets/1_Terrains_and_Fences_32x32.png");
    this.load.image("tilesCity", "tilesets/2_City_Terrains_32x32.png");
    this.load.image("tilesCityProps", "tilesets/3_City_Props_32x32.png");
    this.load.image("tilesBuildings", "tilesets/4_Generic_Buildings_32x32.png");
    this.load.image("tilesShopping", "tilesets/9_Shopping_Center_and_Markets_32x32.png");
    this.load.image("tilesVehicles", "tilesets/10_Vehicles_32x32.png");
    this.load.image(
      "tilesDrinkMachines",
      "tilesets/20_Subway_and_Train_Station_32x32.png",
    );
    // Combat monsters are INFECTED AUTONOMOUS CARS (cropped from the
    // vehicles tileset; top-down, facing up — Monster rotates them to their
    // heading). Names come from the server: Schmesla / ModelT9 / LOLvo.
    this.load.image("mob-schmesla", "sprites/monsters/schmesla.png");
    this.load.image("mob-modelt9", "sprites/monsters/modelt9.png");
    this.load.image("mob-lolvo", "sprites/monsters/lolvo.png");
    // Combat SFX (loaded even on mobile — only MUSIC is desktop-gated).
    this.load.audio("sfx-player-hit", "sounds/hit_player.wav");
    this.load.audio("sfx-mob-hit", "sounds/hit_mob.wav");
    this.load.audio("sfx-mob-engine", "sounds/engine_rev.wav");
    this.load.audio("sfx-mob-pew", "sounds/pew.wav");
    // CACHE-BUST THE MAP. The map URL is otherwise stable forever, so a
    // browser holding an older copy never re-requests it -- and because we
    // shipped it with no Cache-Control for months, browsers applied HEURISTIC
    // freshness (~10% of the file's age = days). A player mid-con would keep
    // rendering a stale world: tilesets the new map declares are missing, so
    // whole districts draw black, and the client disagrees with the server
    // about what is walkable. Headers alone cannot fix an entry already
    // cached, so the URL itself has to change per build.
    const BUILD_ID = process.env.REACT_APP_BUILD_ID || "dev";
    this.load.tilemapTiledJSON("game26", `maps/Game26MAP.tmj?v=${BUILD_ID}`);

    if (!mobileAndTabletCheck()) {
      this.load.audio("0", ["music/some random thing.mp3"]);
      this.load.audio("1", ["music/funny-wip-2.mp3"]);
      this.load.audio("2", ["music/silly-wip-2.mp3"]);
      this.load.audio("3", [
        "music/Gentle_rain_at_twilight_10-28-2023.mp3",
      ]);
      this.load.audio("4", ["music/2nd sem final project_fin.mp3"]);
      this.load.audio("win", ["music/notlikeus.mp3"]);
    }
  }

  create() {
    //  When all the assets have loaded, it's often worth creating global objects here that the rest of the game can use.
    //  For example, you can define global animations here, so we can use them in other scenes.

    // "tilesMob" is only used for INVISIBLE trigger sprites (zones/challenges/
    // storylines/resources, all setAlpha(0)). The game25 dungeon spritesheet it
    // pointed at is gone, which spammed `Texture "__MISSING"` warnings — so
    // register a tiny transparent texture under the same key instead.
    // Dalek projectile: a small red-hot orb, generated (no asset needed).
    if (!this.textures.exists("projectileOrb")) {
      const g = this.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(0xff5533, 0.35);
      g.fillCircle(6, 6, 6);
      g.fillStyle(0xff2200, 1);
      g.fillCircle(6, 6, 3.5);
      g.fillStyle(0xffdd66, 1);
      g.fillCircle(6, 6, 1.6);
      g.generateTexture("projectileOrb", 12, 12);
      g.destroy();
    }
    if (!this.textures.exists("tilesMob")) {
      const g = this.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(0x000000, 0);
      g.fillRect(0, 0, 32, 32);
      g.generateTexture("tilesMob", 32, 32);
      g.destroy();
    }

    //  Move to the MainMenu. You could also swap this for a Scene Transition, such as a camera fade.
    this.scene.start("MainMenu");
  }
}
